import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { login, loading } = useAuth();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm();

  // Detectar se é dispositivo móvel
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Gerar device ID único
  useEffect(() => {
    const deviceId = localStorage.getItem('deviceId') || 
                    `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('deviceId', deviceId);
    setValue('deviceId', deviceId);
  }, [setValue]);

  const onSubmit = async (data) => {
    try {
      const result = await login(data);
      if (!result.success) {
        console.error('Erro no login:', result.error);
      }
    } catch (error) {
      console.error('Erro inesperado:', error);
      toast.error('Erro inesperado. Tente novamente.');
    }
  };

  const handleDemoLogin = () => {
    // Login de demonstração para desenvolvimento
    const demoData = {
      email: 'demo@ttap.com',
      senha: '123456',
      deviceId: localStorage.getItem('deviceId')
    };
    
    setValue('email', demoData.email);
    setValue('senha', demoData.senha);
    setValue('deviceId', demoData.deviceId);
    
    onSubmit(demoData);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
      <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        
        {/* Lado Esquerdo - Informações do Sistema */}
        <div className="hidden lg:block space-y-8">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
                             <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                 <span className="text-white text-2xl font-bold">🚛</span>
               </div>
              <h1 className="text-3xl font-bold text-gray-900">TTAP</h1>
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">
              Sistema de Ponto Eletrônico
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Plataforma completa para gestão de ponto eletrônico, frota e recursos humanos. 
              Controle total da operação com funcionalidades mobile e web.
            </p>
          </div>

          {/* Cards de funcionalidades */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-gray-200">
              <div className="flex items-center space-x-3">
                                 <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                   <span className="text-green-600 text-lg">📱</span>
                 </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Ponto Mobile</h3>
                  <p className="text-sm text-gray-600">Registro com GPS e foto</p>
                </div>
              </div>
            </div>

            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-gray-200">
              <div className="flex items-center space-x-3">
                                 <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                   <span className="text-blue-600 text-lg">🚛</span>
                 </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Gestão de Frota</h3>
                  <p className="text-sm text-gray-600">TCB, rotas e manutenções</p>
                </div>
              </div>
            </div>

            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-gray-200">
              <div className="flex items-center space-x-3">
                                 <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                   <span className="text-purple-600 text-lg">👥</span>
                 </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Recursos Humanos</h3>
                  <p className="text-sm text-gray-600">Colaboradores e benefícios</p>
                </div>
              </div>
            </div>

            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-gray-200">
              <div className="flex items-center space-x-3">
                                 <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                   <span className="text-orange-600 text-lg">📊</span>
                 </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Relatórios</h3>
                  <p className="text-sm text-gray-600">Análises e indicadores</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Lado Direito - Formulário de Login */}
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            
            {/* Header do Formulário */}
            <div className="text-center mb-8">
              <div className="lg:hidden flex justify-center mb-4">
                                 <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center">
                   <span className="text-white text-3xl">🚛</span>
                 </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {isMobile ? 'TTAP' : 'Bem-vindo de volta'}
              </h1>
              <p className="text-gray-600">
                {isMobile ? 'Faça login para acessar o sistema' : 'Entre com suas credenciais para continuar'}
              </p>
            </div>

            {/* Formulário */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              
              {/* Campo Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <div className="relative">
                                     <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                     <span className="text-gray-400 text-lg">📧</span>
                   </div>
                  <input
                    id="email"
                    type="email"
                    {...register('email', {
                      required: 'Email é obrigatório',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Email inválido'
                      }
                    })}
                    className={`block w-full pl-10 pr-3 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      errors.email ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    placeholder="seu@email.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              {/* Campo Senha */}
              <div>
                <label htmlFor="senha" className="block text-sm font-medium text-gray-700 mb-2">
                  Senha
                </label>
                <div className="relative">
                                     <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                     <span className="text-gray-400 text-lg">🔒</span>
                   </div>
                  <input
                    id="senha"
                    type={showPassword ? 'text' : 'password'}
                    {...register('senha', {
                      required: 'Senha é obrigatória',
                      minLength: {
                        value: 6,
                        message: 'Senha deve ter pelo menos 6 caracteres'
                      }
                    })}
                    className={`block w-full pl-10 pr-12 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      errors.senha ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                                         {showPassword ? (
                       <span className="text-gray-400 hover:text-gray-600 text-lg">🙈</span>
                     ) : (
                       <span className="text-gray-400 hover:text-gray-600 text-lg">👁️</span>
                     )}
                  </button>
                </div>
                {errors.senha && (
                  <p className="mt-1 text-sm text-red-600">{errors.senha.message}</p>
                )}
              </div>

              {/* Campo Device ID (oculto) */}
              <input
                type="hidden"
                {...register('deviceId', { required: true })}
              />

              {/* Botão de Login */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Entrando...</span>
                  </div>
                ) : (
                  'Entrar'
                )}
              </button>

              {/* Botão Demo (apenas em desenvolvimento) */}
              {process.env.NODE_ENV === 'development' && (
                <button
                  type="button"
                  onClick={handleDemoLogin}
                  className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-sm"
                >
                  Login Demo
                </button>
              )}
            </form>

            {/* Links de Ajuda */}
            <div className="mt-6 text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Esqueceu sua senha?
              </Link>
            </div>

            {/* Informações de Segurança */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                </div>
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Sistema Seguro</p>
                  <p className="text-blue-700">
                    Suas credenciais são criptografadas e protegidas com as melhores práticas de segurança.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>&copy; 2024 TTAP. Todos os direitos reservados.</p>
            <p className="mt-1">
              Versão {process.env.REACT_APP_VERSION || '1.0.0'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;

